import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Flag, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam', icon: '🗑️' },
  { value: 'inappropriate', label: 'Contenu inapproprié', icon: '🚫' },
  { value: 'harassment', label: 'Harcèlement', icon: '😠' },
  { value: 'misinformation', label: 'Fausses informations', icon: '❌' },
  { value: 'hate_speech', label: 'Discours haineux', icon: '💢' },
  { value: 'violence', label: 'Violence', icon: '⚠️' },
  { value: 'nudity', label: 'Nudité', icon: '🔞' },
  { value: 'scam', label: 'Arnaque', icon: '💰' },
  { value: 'copyright', label: 'Violation de droits d\'auteur', icon: '©️' },
  { value: 'other', label: 'Autre', icon: '📝' },
];

export default function ReportDialog({ 
  open, 
  onOpenChange, 
  targetType, 
  targetId,
  targetName 
}) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Sélectionne une raison');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          reason,
          description: description || null
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Signalement envoyé. Merci!');
        onOpenChange(false);
        setReason('');
        setDescription('');
      } else {
        toast.error(data.detail || 'Erreur lors du signalement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getTargetTypeLabel = () => {
    switch (targetType) {
      case 'user': return 'utilisateur';
      case 'post': return 'publication';
      case 'recipe': return 'recette';
      case 'comment': return 'commentaire';
      case 'video': return 'vidéo';
      case 'stream_message': return 'message';
      case 'message': return 'message';
      default: return 'contenu';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Flag className="h-5 w-5" />
            Signaler ce {getTargetTypeLabel()}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {targetName && <span className="text-white">"{targetName}"</span>}
            <br />
            Aide-nous à garder la communauté sûre.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Reason Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Raison du signalement *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-gray-800 border-gray-700" data-testid="report-reason-select">
                <SelectValue placeholder="Sélectionne une raison" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                {REPORT_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} className="text-white">
                    <span className="flex items-center gap-2">
                      <span>{r.icon}</span>
                      <span>{r.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">
              Détails supplémentaires (optionnel)
            </label>
            <Textarea
              placeholder="Décris le problème..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-gray-800 border-gray-700 min-h-[100px]"
              maxLength={500}
              data-testid="report-description"
            />
            <p className="text-xs text-gray-500 text-right">{description.length}/500</p>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-200">
              Les faux signalements peuvent entraîner des restrictions sur ton compte.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-gray-700"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={loading || !reason}
            data-testid="report-submit-btn"
          >
            {loading ? 'Envoi...' : 'Signaler'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
